<script lang="ts">
  import Table from "../../Table.svelte";
  import Dropdown from "../../Dropdown.svelte";
  import Button from "../../Button.svelte";
  import DateRange from "../../DateRange.svelte";
  import Pagination from "../../Pagination.svelte";
  import SearchInput from "../../SearchInput.svelte";
  import BackButton from "../../BackButton.svelte";
  import { afterUpdate, onMount } from "svelte";
  import { push } from "svelte-spa-router";
  import {
    PayoutService,
    PayoutStatusEnum,
    type DonationPayout,
    type PaginationInfo,
  } from "../../../lib/api/charityServices";

  export let employerId: string;

  // Filter states
  let selectedStatus = "";
  let search = "";
  let startDate = "";
  let endDate = "";
  let loading = false;
  let error: string | null = null;
  let employerName = "";

  // Pagination
  let currentPage = 1;
  let paging: PaginationInfo | null = null;

  // Data
  let payouts: DonationPayout[] = [];

  // Dropdown options - Status options are hardcoded
  let statusOptions = [
    { uuid: "3d8b53f5-11e2-4f8b-9d3b-bb86dcd1e640", name: "Pending" },
    { uuid: "3d8b53f5-11e2-4f8b-9d3b-bb86dcd1e641", name: "Completed" },
    { uuid: "3d8b53f5-11e2-4f8b-9d3b-bb86dcd1e642", name: "Failed" },
  ];

  // Fetch payouts data filtered by employer
  async function fetchPayouts() {
    loading = true;
    error = null;

    try {
      const result = await PayoutService.getPayouts({
        employer: employerId, // Filter by employer UUID
        status: selectedStatus,
        search: search,
        page: currentPage,
        per_page: 10,
        start_date: startDate,
        end_date: endDate,
      });

      if (result.success && result.payouts) {
        payouts = result.payouts;
        paging = result.paging || null;
        
        // Get employer name from first payout if available
        if (payouts.length > 0 && payouts[0].company) {
          employerName = payouts[0].company;
        }
      } else {
        error = result.error || "Failed to load payouts";
        payouts = [];
      }
    } catch (err) {
      console.error("Error fetching payouts:", err);
      error = "An error occurred while fetching payouts";
      payouts = [];
    } finally {
      loading = false;
    }
  }

  // Format date
  function formatDate(dateString: string | undefined): string {
    if (!dateString) return "";

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  }

  // Format currency
  function formatCurrency(amount: number | undefined): string {
    if (amount === undefined) return "";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  }

  // Table columns configuration
  const columns = [
    {
      key: "payout_id",
      label: "Payout ID",
      width: "15%",
      render: (value: string) => `<span class="payout-id">${value}</span>`,
    },
    {
      key: "date",
      label: "Date",
      width: "18%",
      render: (value: string) => `<span class="date-value">${value}</span>`,
    },
    {
      key: "total_amount",
      label: "Total Amount",
      width: "20%",
      render: (value: string) => `<span class="total-amount">${value}</span>`,
    },
    {
      key: "total_donations",
      label: "Total Donations",
      width: "20%",
      render: (value: string) =>
        `<span class="total-donations">${value}</span>`,
    },
    {
      key: "status",
      label: "Status",
      width: "18%",
      align: 'center',
      render: (value: string) => {
        return `<div class="status-badge-wrapper" data-status="${value}"></div>`;
      },
    },
  ];

  // Transform payout data for table display
  function transformPayoutData(payouts: DonationPayout[]) {
    return payouts.map((payout) => ({
      uuid: payout.uuid, // Include UUID for row click
      payout_id: `#P-${payout.uuid?.substring(0, 5) || ""}`,
      date: formatDate(payout.transaction_date) || "N/A",
      total_amount:
        payout.total_amount || formatCurrency(parseFloat(payout.amount || "0")),
      total_donations: `${payout.total_donations || 0} donations`,
      status: payout.donation_payout_status?.name || payout.status?.name || "Pending",
    }));
  }

  function handleRowClick(row: any) {
    // Navigate to payout detail view
    if (row && row.uuid) {
      push(`/payout-detail/${row.uuid}`);
    }
  }

  // Get status badge class
  function getStatusClass(status: string | undefined): string {
    if (!status) return "pending";

    switch (status.toUpperCase()) {
      case "COMPLETED":
        return "completed";
      case "FAILED":
        return "rejected";
      case "PENDING":
      default:
        return "pending";
    }
  }

  // Get status icon based on status
  function getStatusIcon(status: string | undefined): string {
    if (!status) return "";

    switch (status.toUpperCase()) {
      case "COMPLETED":
        return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>';
      case "FAILED":
        return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>';
      case "PENDING":
      default:
        return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/><path d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>';
    }
  }

  // Add status badges to rendered table
  afterUpdate(() => {
    const statusWrappers = document.querySelectorAll(".status-badge-wrapper");
    const transformedData = transformPayoutData(payouts);

    statusWrappers.forEach((wrapper, index) => {
      if (!wrapper.hasChildNodes() && transformedData[index]) {
        const payout = transformedData[index];
        const statusName = payout.status;
        const statusClass = getStatusClass(statusName);
        const statusIcon = getStatusIcon(statusName);

        wrapper.innerHTML = `<div class="status-badge ${statusClass}">${statusIcon}<span>${statusName}</span></div>`;
      }
    });
  });

  // Handle filter changes
  function handleStatusChange(value: string) {
    selectedStatus = value;
    currentPage = 1;
    fetchPayouts();
  }

  function handleSearchInput(event: CustomEvent<string>) {
    search = event.detail;
    currentPage = 1;
    fetchPayouts();
  }

  function handleDateChange(event: CustomEvent) {
    startDate = event.detail.startDate;
    endDate = event.detail.endDate;
    currentPage = 1;
    fetchPayouts();
  }

  function handlePageChange(event: CustomEvent<{ page: number }>) {
    currentPage = event.detail.page;
    fetchPayouts();
  }

  function resetFilters() {
    selectedStatus = "";
    search = "";
    startDate = "";
    endDate = "";
    currentPage = 1;
    fetchPayouts();
  }

  onMount(() => {
    fetchPayouts();
  });
</script>

<div class="employer-payouts-container">
  <div class="top-row">
    <BackButton text="Back" onClick={() => window.history.back()} />
    {#if employerName}
      <span class="employer-context">{employerName}</span>
    {/if}
  </div>

  <!-- Filters Section -->
  <div class="filters-section">
    <div class="filters-left">
      <div class="filter-item">
        <SearchInput
          placeholder="Search"
          bind:value={search}
          width="250px"
          on:search={handleSearchInput}
        />
      </div>

      <div class="filter-item">
        <Dropdown
          options={statusOptions}
          bind:selectedValue={selectedStatus}
          placeholder="Status"
          onSelectionChange={handleStatusChange}
        />
      </div>

      <div class="filter-item date-range-wrapper">
        <DateRange
          bind:startDate
          bind:endDate
          placeholder="Date Range"
          on:change={handleDateChange}
        />
      </div>
    </div>

    <div class="filters-right">
      <Button variant="secondary" on:click={resetFilters}>
        Reset Filters
      </Button>
    </div>
  </div>

  <!-- Table Section -->
  <div class="table-section">
    {#if loading && payouts.length === 0}
      <div class="loading-state">
        <div class="loading-spinner"></div>
        <p>Loading payouts...</p>
      </div>
    {:else if error}
      <div class="error-state">
        <p>{error}</p>
        <Button variant="primary" on:click={fetchPayouts}>Try Again</Button>
      </div>
    {:else if payouts.length === 0}
      <div class="empty-state">
        <div class="empty-icon">
          <svg viewBox="0 0 24 24" width="48" height="48" fill="#9CA3AF">
            <path
              d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-4.86 8.86l-3 3.87L9 13.14 6 17h12l-3.86-5.14z"
            />
          </svg>
        </div>
        <p class="empty-message">No payouts found</p>
        <Button variant="primary" on:click={resetFilters}>Reset Filters</Button>
      </div>
    {:else}
      <div class="table-container">
        {#if loading}
          <div class="table-loading-overlay">
            <div class="loading-spinner"></div>
          </div>
        {/if}
        <Table {columns} data={transformPayoutData(payouts)} onRowClick={handleRowClick} />

        {#if paging && paging.last_page > 1}
          <div class="pagination-wrapper">
            <Pagination
              {currentPage}
              totalPages={paging.last_page}
              maxVisiblePages={7}
              on:pageChange={handlePageChange}
            />
          </div>
        {/if}
      </div>
    {/if}
  </div>
</div>

<style>
  .employer-payouts-container {
    padding: 0;
    background-color: #f7fafc;
    min-height: calc(100vh - 70px);
  }

  .top-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin: 0.5rem 0 1.5rem;
    flex-wrap: wrap;
  }

  /* Keep the back button vertically aligned with the context label;
     the row owns the surrounding spacing. */
  .top-row :global(.back-button) {
    margin: 0;
  }

  .employer-context {
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 500;
  }

  .filters-section {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
    background: white;
    padding: 1.5rem;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .filters-left {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    flex: 1;
  }

  .filters-right {
    display: flex;
    gap: 1rem;
  }

  .filter-item {
    display: flex;
    align-items: center;
  }

  .date-range-wrapper {
    min-width: 200px;
  }

  .table-section {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .loading-state,
  .error-state,
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
  }

  .loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #f3f4f6;
    border-top: 3px solid #f0b429;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .empty-icon {
    margin-bottom: 1rem;
  }

  .empty-message {
    color: #6b7280;
    margin-bottom: 1rem;
  }

  .table-container {
    position: relative;
    min-width: 0; /* Allow table to shrink within flex/grid; prevents unnecessary horizontal scroll */
  }

  .table-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
  }

  .pagination-wrapper {
    margin-top: 1.5rem;
    display: flex;
    justify-content: center;
  }

  :global(.status-badge) {
    display: inline-flex;
    align-items: center;
    padding: 0.375rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1;
    width: fit-content;
    white-space: nowrap;
  }

  :global(.status-badge svg) {
    width: 16px;
    height: 16px;
    margin-right: 0.25rem;
    flex-shrink: 0;
  }

  :global(.status-completed) {
    background-color: #E6F4EA;
    color: #34A853;
  }

  :global(.status-pending) {
    background-color: #FFF8E1;
    color: #F57C00;
  }

  :global(.status-rejected) {
    background-color: #FDEDEB;
    color: #EA4335;
  }

  @media (max-width: 768px) {
    .employer-payouts-container {
      padding: 1rem;
    }

    .filters-section {
      flex-direction: column;
    }

    .filters-left {
      width: 100%;
    }

    .filters-right {
      width: 100%;
      justify-content: flex-end;
    }
  }
</style>

